import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useUserListMutation } from '../redux/features/user/userApi';
import HighchartsAccessibility from 'highcharts/modules/accessibility';

// Initialize accessibility module
if (typeof Highcharts === 'object') {
    HighchartsAccessibility(Highcharts);
}

const Graph = () => {
    const [data, setData] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [totalUsers, setTotalUsers] = useState(0);
    const [growthPercentage, setGrowthPercentage] = useState(0);
    const [getUserList, { isLoading }] = useUserListMutation();

    const years = Array.from(
        { length: 5 },
        (_, i) => new Date().getFullYear() - i
    ).reverse();

    const fetchData = async () => {
        try {
            const response = await getUserList().unwrap();
            if (response?.status === true && response?.data) {
                const monthlyData = processUserData(response.data, selectedYear);
                setData(monthlyData);

                // Calculate total users for selected year
                const total = monthlyData.reduce((sum, item) => sum + item.count, 0);
                setTotalUsers(total);

                // Calculate growth percentage compared to previous year
                if (years.indexOf(selectedYear) > 0) {
                    const prevYear = years[years.indexOf(selectedYear) - 1];
                    const prevYearData = processUserData(response.data, prevYear);
                    const prevTotal = prevYearData.reduce((sum, item) => sum + item.count, 0);
                    const growth = prevTotal > 0 ? Math.round(((total - prevTotal) / prevTotal) * 100) : 100;
                    setGrowthPercentage(growth);
                } else {
                    setGrowthPercentage(0);
                }
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const processUserData = (users, year) => {
        const monthlyCounts = Array(12).fill(0);

        users.forEach(user => {
            if (user.createdAt) {
                const date = new Date(user.createdAt);
                if (date.getFullYear() === year) {
                    const month = date.getMonth();
                    monthlyCounts[month]++;
                }
            }
        });

        return monthlyCounts.map((count, index) => ({
            month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][index],
            count: count
        }));
    };

    useEffect(() => {
        fetchData();
    }, [selectedYear]);

    const chartOptions = {
        chart: {
            type: 'areaspline',
            style: {
                fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif"
            },
            backgroundColor: 'transparent',
            borderRadius: '12px',
            spacing: [20, 20, 20, 20],
            animation: {
                duration: 1000
            }
        },
        title: {
            text: '',
            align: 'left'
        },
        subtitle: {
            text: '',
            align: 'left'
        },
        xAxis: {
            categories: data.map(item => item.month),
            title: {
                text: 'Month',
                style: {
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748B'
                }
            },
            gridLineWidth: 1,
            gridLineColor: 'rgba(100, 116, 139, 0.1)',
            lineColor: 'rgba(100, 116, 139, 0.2)',
            tickColor: 'rgba(100, 116, 139, 0.2)',
            labels: {
                style: {
                    fontSize: '11px',
                    color: '#64748B',
                    fontWeight: '500'
                }
            },
            accessibility: {
                description: 'Months of the year'
            }
        },
        yAxis: {
            title: {
                text: 'Number of Users',
                style: {
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748B'
                }
            },
            gridLineColor: 'rgba(100, 116, 139, 0.1)',
            labels: {
                style: {
                    fontSize: '11px',
                    color: '#64748B',
                    fontWeight: '500'
                }
            },
            min: 0,
            tickInterval: 1,
            accessibility: {
                description: 'Number of registered users'
            }
        },
        tooltip: {
            backgroundColor: '#FFFFFF',
            borderWidth: 0,
            borderRadius: '8px',
            shadow: true,
            style: {
                fontSize: '13px'
            },
            headerFormat: '<span style="font-weight: 600; font-size: 13px; color: #1E293B">{point.key}</span><br/>',
            pointFormat: '<span style="color: #4F46E5; font-weight: 500">{point.y}</span> User(s)',
            animation: true,
            shared: true,
            useHTML: true
        },
        plotOptions: {
            areaspline: {
                fillColor: {
                    linearGradient: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 1
                    },
                    stops: [
                        [0, 'rgba(79, 70, 229, 0.3)'],
                        [1, 'rgba(79, 70, 229, 0.05)']
                    ]
                },
                lineWidth: 3,
                color: '#4F46E5',
                states: {
                    hover: {
                        lineWidth: 4
                    }
                },
                marker: {
                    enabled: true,
                    radius: 5,
                    symbol: 'circle',
                    fillColor: '#FFFFFF',
                    lineWidth: 2,
                    lineColor: '#4F46E5',
                    states: {
                        hover: {
                            enabled: true,
                            radius: 7,
                            fillColor: '#FFFFFF'
                        }
                    }
                },
                threshold: null
            }
        },
        series: [{
            name: 'Registered users',
            data: data.map(item => item.count),
            color: '#4F46E5',
            fillOpacity: 0.3,
            accessibility: {
                description: 'Number of users registered each month'
            }
        }],
        credits: {
            enabled: false
        },
        legend: {
            enabled: false
        },
        responsive: {
            rules: [{
                condition: {
                    maxWidth: 200
                },
                chartOptions: {
                    chart: {
                        spacing: [10, 10, 10, 10]
                    },
                    xAxis: {
                        labels: {
                            rotation: -45
                        }
                    }
                }
            }]
        },
        accessibility: {
            description: 'Chart showing monthly user registration statistics',
            screenReaderSection: {
                beforeChartFormat: '<h5>{chartTitle}</h5>' +
                    '<div>{chartSubtitle}</div>' +
                    '<div>{chartLongdesc}</div>' +
                    '<div>{viewTableButton}</div>'
            }
        }
    };

    return (
        <div className="graph-container w-full h-full bg-white rounded-xl shadow-md border border-slate-200 px-2 py-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800">User Registration Analytics</h2>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        Monthly overview of user registrations for {selectedYear}
                    </p>
                </div>
                <div className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
                    <div className="bg-indigo-50 rounded-lg p-3 flex-1 min-w-[100px] text-center">
                        <p className="text-xs font-medium text-indigo-600">Total Users</p>
                        <p className="text-lg sm:text-xl font-bold text-gray-800 mt-1">{totalUsers}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 flex-1 min-w-[100px] text-center">
                        <p className="text-xs font-medium text-green-600">Growth</p>
                        <p className="text-lg sm:text-xl font-bold text-gray-800 mt-1">
                            {growthPercentage > 0 ? `+${growthPercentage}%` : `${growthPercentage}%`}
                        </p>
                    </div>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm bg-white shadow-sm w-full sm:w-auto"
                        disabled={isLoading}
                    >
                        {years.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {isLoading ? (
                <div className="flex items-center justify-center h-[200px] sm:h-[300px]">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
            ) : (
                <div className="w-full overflow-x-auto">
                    <div className="min-w-[350px] sm:min-w-0">
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={chartOptions}
                            containerProps={{
                                style: {
                                    height: '10rem',
                                    width: '100%',
                                }
                            }}
                        />
                    </div>
                </div>
            )}
            {/* Responsive, attractive table for monthly data */}
            {/* <div className="mt-6 w-full overflow-x-auto">
                <table className="min-w-[350px] w-full text-xs sm:text-sm border border-slate-200 rounded-lg overflow-hidden">
                    <thead className="bg-slate-100">
                        <tr>
                            <th className="py-2 px-3 text-left font-semibold text-slate-600">Month</th>
                            <th className="py-2 px-3 text-left font-semibold text-slate-600">Users</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, idx) => (
                            <tr key={item.month} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="py-2 px-3 whitespace-nowrap">{item.month}</td>
                                <td className="py-2 px-3 whitespace-nowrap font-medium text-indigo-700">{item.count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div> */}
        </div>
    );
}

export default Graph;